import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdminProvider, ImportPreview, ImportResult } from '@shared/types.ts';
import { ApiError, api, type ImportInput } from '@/lib/api.ts';
import { Problem } from './bits.tsx';

/**
 * PULLING CHANNELS OFF AN ACCOUNT.
 *
 * ADD-ONLY, and the screen says so out loud, because the thing somebody fears
 * pressing this button is that it will flatten a lineup they spent an evening
 * arranging. It never deletes and never renumbers what is already there.
 * Pruning is what sync rules are for — they own a number range and a stamp,
 * so they can say what is theirs to remove. An import cannot, so it does not.
 *
 * PREVIEW FIRST, ALWAYS OFFERED. A playlist can hold ten thousand entries and
 * "what did that filter actually match" should not have to be answered by
 * running it. The preview costs the same upstream request the real thing
 * does, and changes nothing.
 */
export function ImportPanel({ provider }: { provider: AdminProvider }) {
  const qc = useQueryClient();

  const [picked, setPicked] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [done, setDone] = useState<ImportResult | null>(null);

  const groups = useQuery({
    queryKey: ['admin', 'providers', provider.id, 'groups'],
    queryFn: () => api.adminProviderGroups(provider.id),
    /* Upstream categories do not change minute to minute, and this is the
       one call on these screens that reaches out of the house. */
    staleTime: 5 * 60_000,
    retry: false,
  });

  const body = (): ImportInput => {
    const out: ImportInput = {};
    if (picked.length) out.groups = picked;
    if (search.trim()) out.search = search.trim();
    const s = Number(start);
    const e = Number(end);
    if (Number.isInteger(s) && Number.isInteger(e) && start !== '' && end !== '' && e >= s) {
      out.numberStart = s;
      out.numberEnd = e;
    }
    return out;
  };

  const ask = useMutation({
    mutationFn: () => api.adminProviderImport(provider.id, { ...body(), preview: true }),
    onSuccess: (r) => { setPreview(r); setDone(null); },
  });

  const run = useMutation({
    mutationFn: (confirmLarge?: boolean) =>
      api.adminProviderImportRun(provider.id, { ...body(), confirmLarge }),
    onSuccess: (r) => {
      setDone(r);
      setPreview(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'providers'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'channels'] });
    },
  });

  /*
   * THE CEILING, surfaced rather than swallowed. Past 500 new channels the
   * server refuses and says how many it would have added; that number is the
   * whole point, so the retry button carries it.
   */
  const tooMany = run.error instanceof ApiError && run.error.status === 409
    ? (run.error.body?.wouldAdd as number | undefined)
    : undefined;

  const rangeBad = start !== '' && end !== '' && Number(end) < Number(start);

  return (
    <div className="import-panel">
      <p className="muted admin-field-hint import-blurb">
        Adds what is missing and touches nothing else — no deletions, no renumbering.
        Channels already here are matched on what {provider.name} calls them, not on
        their name, so a provider tidying its titles does not re-import the lot.
      </p>

      <div className="import-groups">
        {groups.isLoading && <span className="muted">Asking {provider.name}…</span>}
        {groups.error && <Problem error={groups.error} />}
        {groups.data?.length === 0 && (
          <span className="muted">
            This account has no categories to pick from — narrow it with the search box.
          </span>
        )}
        {(groups.data ?? []).map((g) => {
          const on = picked.includes(g.id);
          return (
            <button
              key={g.id}
              type="button"
              className={`group-chip${on ? ' is-on' : ''}`}
              onClick={() => setPicked(
                on ? picked.filter((x) => x !== g.id) : [...picked, g.id],
              )}
            >
              {g.name}
              {g.count != null && <span className="group-chip-count">{g.count}</span>}
            </button>
          );
        })}
      </div>

      {picked.length > 0 && (
        <button className="btn ghost import-clear" onClick={() => setPicked([])}>
          Clear {picked.length} selected
        </button>
      )}

      <div className="import-filters">
        <label className="admin-field">
          <span className="admin-field-label">Name contains</span>
          <input
            className="seerr-input"
            value={search}
            placeholder="optional"
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <label className="admin-field">
          <span className="admin-field-label">Number from</span>
          <input
            className="seerr-input"
            value={start}
            inputMode="numeric"
            placeholder="auto"
            onChange={(e) => setStart(e.target.value.replace(/\D/g, ''))}
          />
        </label>
        <label className="admin-field">
          <span className="admin-field-label">to</span>
          <input
            className="seerr-input"
            value={end}
            inputMode="numeric"
            placeholder="auto"
            onChange={(e) => setEnd(e.target.value.replace(/\D/g, ''))}
          />
        </label>
      </div>
      <p className="muted admin-field-hint">
        Leave the numbers blank and they land after the highest channel you have.
        Give a range and they fill it — which is how a group stays together.
      </p>

      <div className="import-actions">
        <button
          className="btn"
          disabled={ask.isPending || rangeBad}
          onClick={() => ask.mutate()}
        >
          {ask.isPending ? 'Checking…' : 'Preview'}
        </button>
        <button
          className="btn primary"
          disabled={run.isPending || rangeBad || !preview}
          title={preview ? undefined : 'Preview first'}
          onClick={() => run.mutate(undefined)}
        >
          {run.isPending
            ? 'Importing…'
            : preview ? `Import ${preview.wouldAdd}` : 'Import'}
        </button>
      </div>

      {rangeBad && <p className="admin-error">That range runs backwards.</p>}

      {preview && (
        <div className="import-result">
          <strong>{preview.wouldAdd} new</strong> out of {preview.matched} matched
          {preview.alreadyHave > 0 && <> · {preview.alreadyHave} already here</>}
          {preview.sample.length > 0 && (
            <ul className="import-sample">
              {preview.sample.map((c, i) => (
                <li key={i}>{c.name}{c.category ? <span className="muted"> · {c.category}</span> : null}</li>
              ))}
              {preview.wouldAdd > preview.sample.length && (
                <li className="muted">…and {preview.wouldAdd - preview.sample.length} more</li>
              )}
            </ul>
          )}
        </div>
      )}

      {done && (
        <div className="import-result is-done">
          Added <strong>{done.added}</strong> channel{done.added === 1 ? '' : 's'}
          {done.alreadyHave > 0 && <> · {done.alreadyHave} were already here</>}.
        </div>
      )}

      <Problem error={ask.error} />
      {tooMany === undefined && <Problem error={run.error} />}
      {tooMany !== undefined && (
        <p className="admin-error">
          That would add {tooMany} channels.{' '}
          <button className="btn ghost" onClick={() => run.mutate(true)}>
            Add all {tooMany} anyway
          </button>
        </p>
      )}
    </div>
  );
}
